import classNames from 'classnames';
import type { FC, ReactChild } from 'react';
import { memo } from 'react';

import { ReactComponent as IconCommentsBlue } from '../icons/icon-comments-blue.svg';
import { ReactComponent as IconComments } from '../icons/icon-comments.svg';
import { ReactComponent as IconFriendRed } from '../icons/icon-friend-red.svg';
import { ReactComponent as IconFriendWhite } from '../icons/icon-friend-white.svg';
import { ReactComponent as IconFriend } from '../icons/icon-friend.svg';
import { ReactComponent as IconMedalGold } from '../icons/icon-medal-gold.svg';
import { ReactComponent as IconMedalSilver } from '../icons/icon-medal-silver.svg';
import { ReactComponent as IconSettingsDark } from '../icons/icon-settings-dark.svg';
import { ReactComponent as IconSettings } from '../icons/icon-settings.svg';
import { ReactComponent as IconSubjectsBlue } from '../icons/icon-subjects-blue.svg';
import { ReactComponent as IconSubjects } from '../icons/icon-subjects.svg';
import type { QueryParamsMapType } from '../utils/routing';
import { Link } from './Link';

// Don't try to be smarter and use variables for the below classes: tailwind needs to be able to statically analyze the code and find the full class names, including the modifiers like "!", "hover:"...
// Otherwise, the classes will miss in the generated CSS. They will be counted as "unused".
export const tabStylesConfig = {
  pages: {
    linkNormalClasses: 'bg-white hover:border-blue-200',
    linkActiveClasses: 'bg-blue-200 !border-blue-200',
    textNormalClasses: 'text-gray group-hover:text-blue-300',
    textActiveClasses: 'text-blue-300',
    icon: <IconSubjects />,
    hoverIcon: <IconSubjectsBlue />,
    activeIcon: <IconSubjectsBlue />,
  },
  comments: {
    linkNormalClasses: `bg-white hover:border-blue-250`,
    linkActiveClasses: `bg-blue-250 !border-blue-250`,
    textNormalClasses: `text-gray group-hover:text-blue-250`,
    textActiveClasses: 'text-white',
    icon: <IconComments />,
    hoverIcon: <IconCommentsBlue />,
    activeIcon: <IconCommentsBlue />,
  },
  rewards: {
    linkNormalClasses: `bg-white hover:border-yellow-200`,
    linkActiveClasses: `bg-yellow-200 !border-yellow-200`,
    textNormalClasses: `text-gray group-hover:text-yellow-`,
    textActiveClasses: 'text-yellow-400',
    icon: <IconMedalSilver />,
    hoverIcon: <IconMedalGold />,
    activeIcon: <IconMedalGold />,
  },
  friends: {
    linkNormalClasses: `bg-white hover:border-red-300`,
    linkActiveClasses: `bg-red-300 !border-red-300`,
    textNormalClasses: `text-gray group-hover:text-red-300`,
    textActiveClasses: 'text-white',
    icon: <IconFriend />,
    hoverIcon: <IconFriendRed />,
    activeIcon: <IconFriendWhite />,
  },
  preferences: {
    linkNormalClasses: `bg-white hover:border-gray-500`,
    linkActiveClasses: `bg-gray-500 !border-gray-500`,
    textNormalClasses: `text-gray group-hover:text-gray-500`,
    textActiveClasses: 'text-white',
    icon: <IconSettings />,
    hoverIcon: <IconSettingsDark />,
    activeIcon: <IconSettings />,
  },
} as const;

export type TabStyles = keyof typeof tabStylesConfig;

interface Prop {
  tabKey: string;
  styles: keyof typeof tabStylesConfig;
  isActiveTab?: boolean;
  label?: ReactChild;
  to: string;
  params?: QueryParamsMapType;
}

export const ProfileTab: FC<Prop> = memo(function ProfileTab(props) {
  const { tabKey, styles: stylesKey, isActiveTab, label, to, params } = props;

  const styles = tabStylesConfig[stylesKey];

  // shallow seems to have no effect here, although we're supposed to stay on the same page. Weird.
  // https://github.com/vercel/next.js/discussions/14855
  return (
    <Link key={tabKey} to={to} params={params} scroll={false} shallow>
      <a
        className={classNames(
          'ProfileTab group flex flex-wrap min-w-10 text-center rounded-t-md py-3 border-solid border-2 border-b-0 border-transparent justify-center items-center text-gray-500 gap-x-4 gap-y-2 grow',
          isActiveTab ? styles.linkActiveClasses : styles.linkNormalClasses,
        )}
      >
        <div className="flex justify-center items-center w-6 h-6">
          {isActiveTab ? (
            <span>{styles.activeIcon}</span>
          ) : (
            <>
              <span className={classNames('inline-block group-hover:hidden')}>{styles.icon}</span>
              <span className={classNames('hidden group-hover:inline-block')}>
                {styles.hoverIcon}
              </span>
            </>
          )}
        </div>

        <span
          className={classNames(
            'font-bold uppercase text-sm font-source tracking-widest _md:hidden',
            isActiveTab ? styles.textActiveClasses : styles.textNormalClasses,
          )}
        >
          {label}
        </span>
      </a>
    </Link>
  );
});
